import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from './components/providers/theme-provider';
import { BrandThemeProvider } from './components/providers/brand-theme-provider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MainLayout } from './components/layout/MainLayout';
import { RequireAuth } from './features/auth/components/RequireAuth';
import DashboardPage from './pages/DashboardPage';
import { PerfilPage } from './pages/PerfilPage';
import { AuthInitializer } from './components/AuthInitializer';
import { ConfirmProvider } from './components/ui/confirm-dialog';

// Chatbot imports
import { ChatbotInstancesPage } from './features/chatbot/pages/admin/ChatbotInstancesPage';
import { ChatbotInstanceEditPage } from './features/chatbot/pages/admin/ChatbotInstanceEditPage';
import { ChatbotBotsPage } from './features/chatbot/pages/admin/ChatbotBotsPage';
import { ChatbotBotEditPage } from './features/chatbot/pages/admin/ChatbotBotEditPage';
import { ChatbotPage } from './features/chatbot/pages/public/ChatbotPage';
import { UserChatbotsPage } from './features/chatbot/pages/UserChatbotsPage';
import { TenantsPage } from './features/platform-admin/pages/TenantsPage';
import { TenantEditPage } from './features/platform-admin/pages/TenantEditPage';
import { CobradorRecipePage } from './features/platform-admin/pages/CobradorRecipePage';
import { VendedorRecipePage } from './features/platform-admin/pages/VendedorRecipePage';
import { MesaRecipePage } from './features/platform-admin/pages/MesaRecipePage';
import { AsistenteRecipePage } from './features/platform-admin/pages/AsistenteRecipePage';
import { ConserjeRecipePage } from './features/platform-admin/pages/ConserjeRecipePage';
import { RecipesHubPage } from './features/platform-admin/pages/RecipesHubPage';
import { UsersPage } from './features/platform-admin/pages/UsersPage';
import { UserEditPage } from './features/platform-admin/pages/UserEditPage';
import { ChatwootChannelsPage } from './features/chatwoot/pages/ChatwootChannelsPage';
import { WhatsAppChannelsPage } from './features/whatsapp/pages/WhatsAppChannelsPage';
import { TelegramChannelsPage } from './features/telegram/pages/TelegramChannelsPage';
import { FacebookChannelsPage } from './features/facebook/pages/FacebookChannelsPage';
import { InstagramChannelsPage } from './features/instagram/pages/InstagramChannelsPage';
import { ContactAgentDashboardPage } from './features/contact-agent/pages/ContactAgentDashboardPage';
import { ContactAgentImportPage } from './features/contact-agent/pages/ContactAgentImportPage';
import { ContactAgentPaymentPromisesPage } from './features/contact-agent/pages/ContactAgentPaymentPromisesPage';
import { ContactAgentProactiveTemplatesPage } from './features/contact-agent/pages/ContactAgentProactiveTemplatesPage';
import { ContactAgentNewPage } from './features/contact-agent/pages/ContactAgentNewPage';
import { ContactAgentCampaignNewPage } from './features/contact-agent/pages/ContactAgentCampaignNewPage';
import { ContactAgentCampaignDetailPage } from './features/contact-agent/pages/ContactAgentCampaignDetailPage';
import { CampaignsHubPage } from './features/contact-agent/pages/CampaignsHubPage';
import { CampaignTemplatesPage } from './features/contact-agent/components/CampaignTemplatesPage';
import { NoticeProgramNewPage } from './features/contact-agent/pages/NoticeProgramNewPage';
import { NoticeProgramDetailPage } from './features/contact-agent/pages/NoticeProgramDetailPage';
import { ContactAgentContactsPage } from './features/contact-agent/pages/ContactAgentContactsPage';
import { ContactAgentContactTrackingPage } from './features/contact-agent/pages/ContactAgentContactTrackingPage';
import { ContactAgentWorkQueuePage } from './features/contact-agent/pages/ContactAgentWorkQueuePage';
import { InboxPage } from './features/inbox/pages/InboxPage';
import { MesaDashboardPage } from './features/mesa/pages/MesaDashboardPage';
import { MesaAgentPage } from './features/mesa/pages/MesaAgentPage';
import { RequireLeadsFeature } from './features/leads/components/RequireLeadsFeature';
import { LeadsDashboardPage } from './features/leads/pages/LeadsDashboardPage';
import { LeadsAgentPage } from './features/leads/pages/LeadsAgentPage';
import { LeadsBoardPage } from './features/leads/pages/LeadsBoardPage';
import { LeadDetailPage } from './features/leads/pages/LeadDetailPage';
import { LeadsFunnelConfigPage } from './features/leads/pages/LeadsFunnelConfigPage';

function ContactAgentCampaignLegacyRedirect() {
  const campaignId = String(useParams().campaignId ?? '').slice(0, 64);
  return <Navigate to={`/admin/campaigns/${campaignId}`} replace />;
}

function App() {
  return (
    <ThemeProvider defaultTheme='light' storageKey='boilerplate-app-theme'>
      <BrandThemeProvider>
      <ConfirmProvider>
        <ErrorBoundary>
          <Routes>
            {/* Public routes (no auth required) */}
            <Route path='/login' element={<LoginPage />} />
            <Route path='/forgot-password' element={<ForgotPasswordPage />} />
            <Route path='/reset-password' element={<ResetPasswordPage />} />
            <Route path='/chatbot/:instanceId' element={<ChatbotPage />} />

            {/* Protected routes (auth required) */}
            <Route
              element={
                <AuthInitializer>
                  <RequireAuth />
                </AuthInitializer>
              }
            >
              <Route path='/' element={<MainLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path='perfil' element={<PerfilPage />} />
                <Route path='chatbots' element={<UserChatbotsPage />} />

                <Route element={<RequireAuth allowedRoles={['OPERATOR', 'ADMIN', 'SUPERADMIN']} />}>
                  <Route path='admin/inbox' element={<InboxPage />} />
                  {/* Tablero de leads en modo lectura: el vendedor (OPERATOR) ve los
                      casos derivados; la configuración del embudo sigue solo para admin. */}
                  <Route element={<RequireLeadsFeature />}>
                    <Route path='admin/leads/board' element={<LeadsBoardPage />} />
                    <Route path='admin/leads/:leadId' element={<LeadDetailPage />} />
                  </Route>
                </Route>

                {/* Admin chatbot routes */}
                <Route element={<RequireAuth allowedRoles={['ADMIN', 'SUPERADMIN']} />}>
                  <Route path='admin/chatbot' element={<ChatbotInstancesPage />} />
                  <Route path='admin/chatbot/bots' element={<ChatbotBotsPage />} />
                  <Route path='admin/chatbot/bots/:botId' element={<ChatbotBotEditPage />} />
                  <Route path='admin/chatbot/:id' element={<ChatbotInstanceEditPage />} />
                  <Route path='admin/users' element={<UsersPage />} />
                  <Route path='admin/users/:id' element={<UserEditPage />} />
                  <Route path='admin/helpdesk' element={<Navigate to='/admin/mesa' replace />} />
                  <Route path='admin/mesa' element={<MesaDashboardPage />} />
                  <Route path='admin/mesa/agents/new' element={<MesaAgentPage />} />
                  <Route path='admin/mesa/agents/:agentId' element={<MesaAgentPage />} />
                  <Route element={<RequireLeadsFeature />}>
                    <Route path='admin/leads' element={<LeadsDashboardPage />} />
                    <Route path='admin/leads/agents/new' element={<LeadsAgentPage />} />
                    <Route path='admin/leads/agents/:agentId' element={<LeadsAgentPage />} />
                    <Route path='admin/leads/funnel-config' element={<LeadsFunnelConfigPage />} />
                  </Route>
                  <Route path='admin/chatwoot' element={<ChatwootChannelsPage />} />
                  <Route path='admin/whatsapp' element={<WhatsAppChannelsPage />} />
                  <Route path='admin/telegram' element={<TelegramChannelsPage />} />
                  <Route path='admin/facebook' element={<FacebookChannelsPage />} />
                  <Route path='admin/instagram' element={<InstagramChannelsPage />} />
                  <Route path='admin/contact-agent' element={<ContactAgentDashboardPage />} />
                  <Route path='admin/contact-agent/agents/new' element={<ContactAgentNewPage />} />
                  <Route path='admin/contact-agent/agents/:agentId' element={<ContactAgentNewPage />} />
                  <Route path='admin/campaigns' element={<CampaignsHubPage />} />
                  <Route path='admin/campaigns/new' element={<ContactAgentCampaignNewPage />} />
                  <Route path='admin/campaigns/templates' element={<CampaignTemplatesPage />} />
                  <Route path='admin/campaigns/dispatches/new' element={<NoticeProgramNewPage />} />
                  <Route path='admin/campaigns/dispatches/:programId' element={<NoticeProgramDetailPage />} />
                  <Route path='admin/campaigns/:campaignId' element={<ContactAgentCampaignDetailPage />} />
                  <Route
                    path='admin/contact-agent/campaigns/new'
                    element={<Navigate to='/admin/campaigns/new' replace />}
                  />
                  <Route
                    path='admin/contact-agent/campaigns/:campaignId'
                    element={<ContactAgentCampaignLegacyRedirect />}
                  />
                  <Route path='admin/contact-agent/inbox' element={<ContactAgentWorkQueuePage />} />
                  <Route path='admin/contact-agent/import' element={<ContactAgentImportPage />} />
                  <Route path='admin/contact-agent/contacts' element={<ContactAgentContactsPage />} />
                  <Route
                    path='admin/contact-agent/contacts/:contactId'
                    element={<ContactAgentContactTrackingPage />}
                  />
                  <Route
                    path='admin/contact-agent/exceptions'
                    element={<Navigate to='/admin/contact-agent/inbox' replace />}
                  />
                  <Route
                    path='admin/contact-agent/payment-promises'
                    element={<ContactAgentPaymentPromisesPage />}
                  />
                  <Route
                    path='admin/contact-agent/proactive-templates'
                    element={<ContactAgentProactiveTemplatesPage />}
                  />
                </Route>

                <Route element={<RequireAuth allowedRoles={['SUPERADMIN']} />}>
                  <Route path='admin/tenants' element={<TenantsPage />} />
                  <Route path='admin/tenants/:id' element={<TenantEditPage />} />
                  <Route path='admin/recetas' element={<RecipesHubPage />} />
                  <Route path='admin/recetas/asistente' element={<AsistenteRecipePage />} />
                  <Route path='admin/recetas/conserje' element={<ConserjeRecipePage />} />
                  <Route path='admin/recetas/cobrador' element={<CobradorRecipePage />} />
                  <Route path='admin/recetas/vendedor' element={<VendedorRecipePage />} />
                  <Route path='admin/recetas/mesa' element={<MesaRecipePage />} />
                </Route>
              </Route>
            </Route>

            {/* Fallback to login */}
            <Route path='*' element={<Navigate to='/login' replace />} />
          </Routes>
        </ErrorBoundary>
      </ConfirmProvider>
      </BrandThemeProvider>
    </ThemeProvider>
  );
}

export default App;
